/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
// Extends From
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
// External Imports
import moment from 'moment';
// Internal Imports
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import {
  BehaviourType,
  IncidentOutcome,
  IncidentStatus,
  IncidentTimePeriod,
  BehavioursOfConcern,
  SeriousBehavioursOfConcern,
  BehaviourCategoriesNSW,
} from 'linced-app/enums/behaviours';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { generatePost } from 'linced-app/utils/incidents';
import { isEmpty } from '@ember/utils';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class BehaviourIncidentEntry extends Component {
  @action
  setCreatePost(value) {
    this.createPost = value;
  }

  // Services
  @service
  behaviourDates;

  @service
  dates;

  @service
  options;

  @service
  permissions;

  @service
  roles;

  @service
  session;

  @service
  store;

  @service
  toaster;

  BehaviourType = BehaviourType;
  BehavioursOfConcern = BehavioursOfConcern;
  BehaviourCategoriesNSW = BehaviourCategoriesNSW;
  IncidentOutcome = IncidentOutcome;
  IncidentStatus = IncidentStatus;
  IncidentTimePeriod = IncidentTimePeriod;
  Scopes = Scopes;
  SeriousBehavioursOfConcern = SeriousBehavioursOfConcern;
  learner = null;
  display = 'button';
  @tracked mode = 'record';
  @tracked step = 'details';
  @tracked
  behaviourIncident = null;
  @tracked
  date = null;
  @tracked
  tags = [];
  learners = [];
  @tracked behaviourPage = null;
  @tracked
  teachers = [];
  @tracked
  dialogOpen = false;
  overrideJurisdiction = 0;
  @tracked
  createPost = true;
  editorOptions = Object.freeze(defaultTinyMCEConfig());
  @optionAlias('nswDOE:behaviours:categories')
  customCategories;

  @computed('behaviourIncident.outcome', 'session.{roles.[],scopes.[]}')
  get isReadonly() {
    if (this.behaviourIncident.outcome) {
      return !(
        this.roles.has(Staff.PRINCIPAL) ||
        this.permissions.meet(Scopes.LINCED_ADMIN)
      );
    }

    return false;
  }

  @computed('customCategories')
  get customBehaviourCategories() {
    let mappedCategories = [];
    if (this.customCategories && this.customCategories.length > 0) {
      mappedCategories = this.customCategories.map((option) => {
        return {
          value: BehaviourCategoriesNSW.OTHER,
          label: option.value,
        };
      });
    }
    return mappedCategories;
  }

  @computed('customBehaviourCategories')
  get behaviourCategories() {
    let behavioursOfConcern = BehavioursOfConcern.values;
    let seriousBehavioursOfConcern = SeriousBehavioursOfConcern.values;
    return [
      ...behavioursOfConcern,
      ...seriousBehavioursOfConcern,
      ...this.customBehaviourCategories,
    ];
  }

  @computed('customBehaviourCategories')
  get groupedBehaviourCategories() {
    let behavioursOfConcern = {
      groupName: 'Behaviours of Concern',
      options: BehavioursOfConcern.values,
    };
    let seriousBehavioursOfConcern = {
      groupName: 'Serious Behaviours of Concern',
      options: SeriousBehavioursOfConcern.values,
    };
    let customBehavioursOfConcern = {
      groupName: 'Custom Behaviour Categories',
      options: this.customBehaviourCategories,
    };
    return [
      behavioursOfConcern,
      seriousBehavioursOfConcern,
      customBehavioursOfConcern,
    ];
  }

  @computed('behaviourIncident.postId')
  get behaviourPost() {
    if (this.get('behaviourIncident.post.id')) {
      return this.get('behaviourIncident.post');
    }
  }

  @computed('behaviourPost.page', 'behaviourPage')
  get computedBehaviourPage() {
    if (this.behaviourPost) {
      return this.get('behaviourPost.page');
    }
    return this.behaviourPage;
  }

  @computed('step', 'computedBehaviourPage', 'behaviourIncident.outcome')
  get displayNextButton() {
    switch (this.step) {
      case 'details':
        if (isEmpty(this.computedBehaviourPage)) {
          let outcome = this.get('behaviourIncident.outcome');
          if (!outcome) {
            return false;
          }
          if (
            outcome === IncidentOutcome.SUSPENSION ||
            outcome === IncidentOutcome.EXPULSION
          ) {
            return false;
          }
        }
        break;
      case 'outcome':
        if (isEmpty(this.computedBehaviourPage)) {
          return false;
        }
        break;
      case 'tags':
        return false;
    }
    return true;
  }

  @computed('step', 'computedBehaviourPage', 'behaviourPost')
  get showBehaviourPostToggle() {
    if (this.step === 'details') {
      return (
        !isEmpty(this.computedBehaviourPage) && isEmpty(this.behaviourPost)
      );
    }
    return false;
  }

  @computed(
    'step',
    'mode',
    'behaviourIncident.{reportedBy,referredTo,learner,type,date,category,entryNote}',
  )
  get isValid() {
    switch (this.step) {
      case 'details':
        if (this.mode === 'record') {
          if (isEmpty(this.get('behaviourIncident.learner.id'))) {
            return {
              message: 'A learner must be selected to file an incident',
              value: false,
            };
          }
          if (this.get('behaviourIncident.type') === BehaviourType.NEGATIVE) {
            if (!this.get('behaviourIncident.category')) {
              return {
                message: 'Negative behaviour incidents must specify a category',
                value: false,
              };
            }
          }
        }
        if (!(
          this.behaviourIncident.get('reportedBy.id') &&
          this.behaviourIncident.get('referredTo.id') &&
          this.behaviourIncident.get('type') &&
          this.behaviourIncident.get('date')
        )) {
          return {
            message: 'Please fill out all required fields before submitting',
            value: false,
          };
        }
        break;
      default:
    }

    return {
      value: true,
    };
  }

  // Lifted from new-post component
  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  saveInProgress;

  // Lifted from common new-post
  @computed('computedBehaviourPage.tagCategoryIds.[]')
  get tagListClasses() {
    let numCategories = this.get('computedBehaviourPage.tagCategoryIds.length');
    switch (numCategories) {
      case 1:
        return 'flex-100';
      case 2:
        return 'flex-xs-100 flex-gt-xs-45';
      case 3:
        return 'flex-xs-100 flex-gt-xs-30';
      case 4:
        return 'flex-xs-100 flex-gt-xs-30 flex-gt-md-25';
      default:
        // 5 or more categories - limit to 5 per row
        return 'flex-xs-100 flex-gt-xs-30 flex-gt-md-20';
    }
  }

  @action
  openDialog() {
    let now = moment();
    if (this.behaviourPage) {
      this.behaviourPage.reload();
    }
    switch (this.mode) {
      case 'record':
        this.behaviourIncident = this.store.createRecord(
          'attendance/behaviour-incident',
          {
            schoolId: this.get('session.school'),
            date: now.unix(),
            authorId: this.get('session.person'),
            reportedBy: this.get('session.person'),
            type: BehaviourType.NEGATIVE,
            tagIds: [],
            entryNote: this.get('behaviourPage.postTemplate'),
            jurisdiction: this.jurisdiction,
          },
        );

        this.tags = [];
        this.date = {
          date: now.format('YYYY-MM-DD'),
          time: now.format('HH:mm'),
        };
        break;
      case 'edit': {
        let date = this.get('behaviourIncident.date');
        // tagIds is an async hasMany (a PromiseManyArray), which has no array
        // methods; toggleTag needs a plain array, so copy the loaded tags.
        let tags = [];
        if (!isEmpty(this.behaviourPost)) {
          this.get('behaviourPost.tagIds').then((loaded) => {
            this.tags = loaded.slice();
          });
        }
        let createPost = !isEmpty(this.behaviourPost);

        this.date = {
          date: moment.unix(date).format('YYYY-MM-DD'),
          time: moment.unix(date).format('HH:mm'),
        };

        this.tags = tags;
        this.createPost = createPost;
        break;
      }
    }

    this.teachers = this.store
      .peekAll('person')
      .filter((person) => person.type === PersonType.STAFF);

    this.dialogOpen = true;
    this.step = 'details';
  }

  @action
  closeDialog() {
    if (isEmpty(this.get('behaviourIncident.id'))) {
      this.behaviourIncident.deleteRecord();
      this.behaviourIncident = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.behaviourIncident);
    }
    this.dialogOpen = false;
  }

  @action
  save() {
    this.saveInProgress = true;
    return Promise.resolve()
      .then(() => {
        let incident = this.behaviourIncident;

        if (this.mode === 'record') {
          incident.set('jurisdiction', this.get('session.school.jurisdiction'));
          incident.set('status', IncidentStatus.OPEN);
          if (incident.type !== BehaviourType.NEGATIVE) {
            incident.set('category', 0);
          }
        }

        incident.set(
          'date',
          moment(
            `${this.date.date} ${this.date.time}`,
            'YYYY-MM-DD HH:mm',
          ).unix(),
        );
        if (this.computedBehaviourPage && this.createPost) {
          let content = generatePost(incident);
          let subject =
            incident.get('learner.fullName') +
            ', ' +
            moment.unix(incident.get('date')).format('DD/MM/YYYY LT');

          if (isEmpty(this.behaviourPost)) {
            const postParams = {
              postType: PostType.POST,
              status: PostStatus.DRAFT,
              schoolId: this.get('session.school'),
              displayDate: moment
                .unix(incident.get('date'))
                .format('YYYY-MM-DD'),
              authorId: this.get('session.person'),
              personId: incident.get('learnerId'),
              pageId: this.behaviourPage,
              tagIds: this.tags,
              content,
              subject: subject,
              behaviour: {
                type: incident.get('type'),
                category: incident.get('category'),
              },
              jurisdiction: Jurisdiction.AU_NSW,
              isSickbay: true, // todo: change isSickbay to isSpecialUse
            };
            let post = this.store.createRecord('posts/post', postParams);

            return post
              .save()
              .then((res) => {
                if (isEmpty(incident.get('post.id'))) {
                  incident.set('post', res);
                }
              })
              .catch(() => {
                this.toaster.error(
                  'Error saving behaviour post, please try again.',
                );
              });
          }

          this.behaviourPost.then((post) => {
            post.set('content', content);
            post.set('subject', subject);

            return post
              .save()
              .then((res) => {
                if (isEmpty(incident.get('post.id'))) {
                  incident.set('post', res);
                }
              })
              .catch(() => {
                this.toaster.error(
                  'Error saving behaviour post, please try again.',
                );
              });
          });
        }
      })
      .then(() => {
        return this.behaviourIncident.save();
      })
      .then(() => {
        return this.existingIncidents.update().then(() => {
          this.toaster.success('Behaviour incident saved');
          if (this.mode === 'record') {
            this.behaviourIncident = null;
            this.createPost = true;
          }
          this.dialogOpen = false;
          this.saveInProgress = false;
        });
      })
      .catch(() => {
        this.toaster.error('Error saving behaviour incident');
      });
  }

  @action
  toggleTag(tag) {
    if (this.tags.includes(tag)) {
      this.tags = this.tags.filter((t) => t !== tag);
    } else {
      this.tags = [...this.tags, tag];
    }
  }

  @action
  nextStep() {
    switch (this.step) {
      case 'details': {
        let outcome = this.get('behaviourIncident.outcome');
        if (outcome) {
          if (
            outcome !== IncidentOutcome.SUSPENSION &&
            outcome !== IncidentOutcome.EXPULSION
          ) {
            this.step = 'outcome';
            return;
          }
        }
        this.step = 'tags';
        return;
      }
      case 'outcome':
        this.step = 'tags';
        return;
      case 'tags':
        return;
      default:
        return;
    }
  }

  @action
  previousStep() {
    switch (this.step) {
      case 'tags': {
        let outcome = this.get('behaviourIncident.outcome');
        if (outcome) {
          if (
            outcome !== IncidentOutcome.SUSPENSION &&
            outcome !== IncidentOutcome.EXPULSION
          ) {
            this.step = 'outcome';
            return;
          }
        }
        this.step = 'details';
        return;
      }
      case 'outcome':
        this.step = 'details';
        return;
      case 'details':
        return;
      default:
        return;
    }
  }

  @action
  removeLearner() {
    this.behaviourIncident.set('learner', null);
  }

  @action
  removeReportedBy() {
    this.behaviourIncident.set('reportedBy', null);
  }

  @action
  removeReferredTo() {
    this.behaviourIncident.set('referredTo', null);
  }

  @action
  removeIncidentDate() {
    this.behaviourIncident.set('date', null);
  }

  @action
  setCategory(category) {
    let customCategory = '';
    if (category.value === BehaviourCategoriesNSW.OTHER) {
      customCategory = category.label;
    }
    this.behaviourIncident.setProperties({
      category: category.value,
      customCategory,
    });
  }

  @action
  setStatus(status) {
    this.behaviourIncident.set('status', status.value);
  }

  @action
  setOutcome(outcome) {
    this.behaviourIncident.set('outcome', outcome.value);
  }

  @action
  setType(type) {
    this.behaviourIncident.set('type', type.value);
  }

  @action
  setTimePeriod(timePeriod) {
    this.behaviourIncident.set('timePeriod', timePeriod.value);
  }
}
